/**
 * Takes a Firebase Timestamp or Date object, and returns a valid Date instance.
 */
export const toDate = input => {
	if (!input) return null;

	if (input instanceof Date) {
		return isNaN(input.getTime()) ? null : input;
	}

	// Firebase Timestamp instance
	if (typeof input === "object" && typeof input.toDate === "function") {
		const date = input.toDate();
		return isNaN(date.getTime()) ? null : date;
	}

	// Firebase Timestamp JSON structure (plain object with seconds/nanoseconds)
	if (
		typeof input === "object" &&
		typeof input.seconds === "number" &&
		typeof input.nanoseconds === "number"
	) {
		const millis =
			input.seconds * 1000 + Math.floor(input.nanoseconds / 1_000_000);
		const date = new Date(millis);
		return isNaN(date.getTime()) ? null : date;
	}

	// ISO string or number
	if (typeof input === "string" || typeof input === "number") {
		const date = new Date(input);
		return isNaN(date.getTime()) ? null : date;
	}

	console.warn("Invalid date input:", input);
	return null;
};

/**
 * Returns the year (e.g., "2025")
 */
export const getYear = timestamp => {
	const date = toDate(timestamp);
	return date ? date.getFullYear() : null;
};

/**
 * Returns "YYYY-MM" (e.g., "2025-05")
 */
export const getYearMonth = timestamp => {
	const date = toDate(timestamp);
	if (!date) return null;
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	return `${year}-${month}`;
};

/**
 * Returns "YYYY-MM-DD" (e.g., "2025-05-19")
 */
export const getYearMonthDay = timestamp => {
	const date = toDate(timestamp);
	if (!date) return null;
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

/**
 * Returns a localized date string with short day + short month + year.
 * Example: "Sat 17 Sep 2025" (en) or "sam. 17 sept. 2025" (fr)
 * @param {Date|Timestamp|string} timestamp
 * @param {string} locale - e.g., "fr", "en"
 */
export const formatDateLocalized = (timestamp, locale = "fr") => {
	const date = toDate(timestamp);
	if (!date) return ""; // Returns an empty string instead of null

	try {
		const options = {
			weekday: "short",
			day: "2-digit",
			month: "short",
			year: "numeric",
		};
		return new Intl.DateTimeFormat(locale, options).format(date);
	} catch (error) {
		console.warn(
			"Erreur de formatage de date:",
			error,
			"pour la valeur:",
			timestamp
		);
		return ""; // Returns an empty string in case of an error
	}
};

/**
 * Returns full weekday name + day number (e.g., "Friday 27" or "vendredi 27")
 * @param {Date|Timestamp|string} timestamp
 * @param {string} locale - e.g., "fr", "en"
 */
export const getFullWeekdayWithDayNumber = (timestamp, locale = "fr") => {
	const date = toDate(timestamp);
	if (!date) return "";

	try {
		const weekday = new Intl.DateTimeFormat(locale, {
			weekday: "long",
		}).format(date);

		const day = String(date.getDate()).padStart(2, "0");

		return `${weekday} ${day}`;
	} catch (error) {
		console.warn(
			"Erreur de formatage de jour:",
			error,
			"pour la valeur:",
			timestamp
		);
		return "";
	}
};

/**
 * Returns the capitalized localized month name (e.g., "Mai" or "May")
 * @param {Date|Timestamp|string} timestamp - Date or timestamp
 * @param {string} locale - Language code (e.g., "fr", "en")
 * @param {Object} options - Intl.DateTimeFormat options (optional)
 * @returns {string|null}
 */
export const getLocalizedMonthName = (
	timestamp,
	locale = "fr",
	options = {}
) => {
	const date = toDate(timestamp);
	if (!date) return "";

	try {
		const defaultOptions = { month: "long" };
		const month = new Intl.DateTimeFormat(locale, {
			...defaultOptions,
			...options,
		}).format(date);

		if (typeof month !== "string") return "";

		// Capitalize first letter only
		return month.charAt(0).toUpperCase() + month.slice(1).toLowerCase();
	} catch (error) {
		console.warn(
			"Erreur de formatage de mois:",
			error,
			"pour la valeur:",
			timestamp
		);
		return "";
	}
};

/**
 * Returns the capitalized localized month name from a YYYY-MM string
 * @param {string} yearMonth - Format "YYYY-MM" (e.g., "2025-01")
 * @param {string} locale - Language code (e.g., "fr", "en")
 * @returns {string}
 */
export const getLocalizedMonthNameFromYearMonth = (
	yearMonth,
	locale = "fr"
) => {
	if (!yearMonth || typeof yearMonth !== "string") return "";

	try {
		const [year, month] = yearMonth.split("-");
		if (!year || !month) return "";

		const yearNum = parseInt(year, 10);
		const monthNum = parseInt(month, 10);

		if (isNaN(yearNum) || isNaN(monthNum) || monthNum < 1 || monthNum > 12) {
			return "";
		}

		// Create a date with year and month (day = 1)
		const date = new Date(yearNum, monthNum - 1, 1);

		const monthName = new Intl.DateTimeFormat(locale, {
			month: "long",
		}).format(date);

		// Capitalize first letter only
		return monthName.charAt(0).toUpperCase() + monthName.slice(1).toLowerCase();
	} catch (error) {
		console.warn(
			"Month formatting error since YYYY-MM:",
			error,
			"for value:",
			yearMonth
		);
		return "";
	}
};
